import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import CustomDropdown from "../../../Ui_Components/CustomDropdown";
import { DropdownOption } from "../../../Ui_Components/CustomDropdown";
import FormInput from "../components/FormInput";
import FormDropdown from "../components/FormDropdown";
import DOBPicker from "../components/DOBPicker";
import { BasicInfoData, BasicInfoErrors, DocumentEntry, createId } from "../types";
import { getStateList, getDocumentTypes } from "../../../../services/team";

interface BasicInfoStepProps {
  data: BasicInfoData;
  onChange: (data: BasicInfoData) => void;
  errors?: BasicInfoErrors;
  onError?: (title: string, message?: string) => void;
}

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

interface ApiState {
  state_id: number;
  state: string;
}

interface ApiDocumentType {
  document_id: number;
  document_name: string;
}

const titleOptions: DropdownOption[] = [
  { value: "mr", label: "Mr" },
  { value: "mrs", label: "Mrs" },
  { value: "ms", label: "Ms" },
  { value: "dr", label: "Dr" },
];

const genderOptions: DropdownOption[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const titleCase = (s: string) =>
  s.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

const sanitizePhone = (v: string) => v.replace(/\D/g, "").slice(0, 10);

const formatAadhar = (v: string) => {
  const digits = v.replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

const formatPan = (v: string) => {
  const cleaned = v.toUpperCase().replace(/[^A-Z0-9]/g, "");
  let result = "";
  for (const ch of cleaned) {
    const pos = result.length;
    if (pos >= 10) break;
    if (pos < 5 && /[A-Z]/.test(ch)) result += ch;
    else if (pos >= 5 && pos < 9 && /[0-9]/.test(ch)) result += ch;
    else if (pos === 9 && /[A-Z]/.test(ch)) result += ch;
  }
  return result;
};

const isAadharDoc = (name: string) => /aadh/i.test(name);
const isPanDoc = (name: string) => /\bpan\b/i.test(name);
const isDrivingLicenseDoc = (name: string) =>
  /driv|licen[cs]e|\bdl\b/i.test(name);

const formatDrivingLicense = (v: string) => {
  const cleaned = v.toUpperCase().replace(/[^A-Z0-9]/g, "");
  let result = "";
  for (const ch of cleaned) {
    const pos = result.replace(/\s/g, "").length;
    if (pos >= 15) break;
    if (pos < 2 && /[A-Z]/.test(ch)) result += ch;
    else if (pos >= 2 && pos < 4 && /[0-9]/.test(ch)) result += ch;
    else if (pos >= 4 && /[0-9]/.test(ch)) {
      if (pos === 4 && !result.endsWith(" ")) result += " ";
      result += ch;
    }
  }
  return result;
};

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({ data, onChange, errors = {}, onError }) => {
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [stateOptions, setStateOptions] = useState<DropdownOption[]>([]);
  const [documentTypes, setDocumentTypes] = useState<ApiDocumentType[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchStates = async () => {
      try {
        const json = await getStateList();
        if (cancelled || json.success !== "1") return;
        const list: DropdownOption[] = (json.nutritionist_state_data ?? []).map(
          (s: ApiState) => {
            const label = titleCase(s.state);
            return { value: label, label };
          },
        );
        setStateOptions(list);
      } catch {
        /* leave empty on failure */
      }
    };

    const fetchDocumentTypes = async () => {
      try {
        const json = await getDocumentTypes();
        if (cancelled || json.success !== "1") return;
        setDocumentTypes(json.documents ?? []);
      } catch {
        /* leave empty on failure */
      }
    };

    fetchStates();
    fetchDocumentTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const documentTypeOptions: DropdownOption[] = documentTypes.map((d) => ({
    value: d.document_name,
    label: d.document_name,
  }));

  const update = (patch: Partial<BasicInfoData>) =>
    onChange({ ...data, ...patch });

  /* ── Photo upload ── */
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError?.(
        "File too large",
        "Profile picture must be below 5 MB."
      );
      e.target.value = "";
      return;
    }
    const preview = URL.createObjectURL(file);
    update({ photo: file, photoPreview: preview });
  };

  /* ── Documents ── */
  const handleAddDocument = (type: string) => {
    if (!type) return;
    const newDoc: DocumentEntry = {
      id: createId(),
      type,
      number: "",
      file: null,
      fileName: "",
    };
    update({ documents: [...data.documents, newDoc] });
  };

  const handleDocumentChange = (
    id: string,
    patch: Partial<DocumentEntry>
  ) => {
    update({
      documents: data.documents.map((d) =>
        d.id === id ? { ...d, ...patch } : d
      ),
    });
  };

  const handleDocumentDelete = (id: string) => {
    update({ documents: data.documents.filter((d) => d.id !== id) });
  };

  const handleDocumentFileChange = (
    id: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      onError?.(
        "File too large",
        "Document file must be below 5 MB."
      );
      e.target.value = "";
      return;
    }
    handleDocumentChange(id, { file, fileName: file.name });
  };

  const usedDocumentTypes = data.documents.map((d) => d.type);
  const availableDocumentOptions = documentTypeOptions.filter(
    (opt) => !usedDocumentTypes.includes(opt.value)
  );

  return (
    <div className="space-y-4">
      {/* ── Personal details card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Photo upload */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            <div
              onClick={() => photoInputRef.current?.click()}
              className="w-36 h-40 border-2 border-dashed border-[#D1D5DB] rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#0DB5B5] transition-colors overflow-hidden"
            >
              {data.photoPreview ? (
                <img
                  src={data.photoPreview}
                  alt="Passport photo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-[#F3F4F6] flex items-center justify-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                        stroke="#9CA3AF"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-xs 3xl:text-sm text-[#646464] font-inter text-center px-2 leading-tight">
                    Click to upload passport photo
                  </span>
                </>
              )}
            </div>
            <p className="text-[10px] 3xl:text-xs text-[#9CA3AF] font-inter">
              JPG/PNG · Max 5 MB
            </p>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Fields */}
          <div className="flex-1 grid grid-cols-1 gap-4">
            {/* Title */}
            <FormDropdown
              label="Title"
              options={titleOptions}
              value={data.title}
              onChange={(v) => update({ title: v })}
              placeholder="eg. Mr"
              className="max-w-[200px]"
              error={errors.title}
            />

            {/* First name / Last name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput
                label="First name"
                value={data.firstName}
                onChange={(v) => update({ firstName: v })}
                placeholder="eg. John"
                error={errors.firstName}
              />
              <FormInput
                label="Last name"
                value={data.lastName}
                onChange={(v) => update({ lastName: v })}
                placeholder="eg. Doe"
                error={errors.lastName}
              />
            </div>

            {/* Phone / Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormInput
                label="Phone number"
                value={data.phone}
                onChange={(v) => update({ phone: sanitizePhone(v) })}
                placeholder="eg. 7869369854"
                type="tel"
                error={errors.phone}
              />
              <FormInput
                label="Email address"
                value={data.email}
                onChange={(v) => update({ email: v })}
                placeholder="eg. john@gmail.com"
                type="email"
                error={errors.email}
              />
            </div>

            {/* DOB / Gender */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                  DOB
                </label>
                <DOBPicker
                  value={data.dob}
                  onChange={(v) => update({ dob: v })}
                  error={errors.dob}
                  maxDate={new Date()}
                />
              </div>
              <FormDropdown
                label="Gender"
                options={genderOptions}
                value={data.gender}
                onChange={(v) => update({ gender: v })}
                placeholder="eg. Male"
                error={errors.gender}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Address card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
        <FormInput
          label="Address"
          value={data.address}
          onChange={(v) => update({ address: v })}
          placeholder="eg. 13D, MR road"
          error={errors.address}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormInput
            label="City"
            value={data.city}
            onChange={(v) => update({ city: v })}
            placeholder="eg. Mumbai"
            error={errors.city}
          />
          <FormDropdown
            label="State"
            options={stateOptions}
            value={data.state}
            onChange={(v) => update({ state: v })}
            placeholder="eg. Maharashtra"
            searchable
            error={errors.state}
          />
          <FormInput
            label="Pincode"
            value={data.pincode}
            onChange={(v) =>
              update({ pincode: v.replace(/\D/g, "").slice(0, 6) })
            }
            placeholder="eg. 400001"
            error={errors.pincode}
          />
        </div>
      </div>

      {/* ── Documents card ── */}
      <div className="bg-white rounded-[6px] border border-[#F3F4F6] shadow-sm p-6 space-y-4">
        {/* Existing document rows */}
        {data.documents.map((doc) => {
          const docName = doc.type;
          const aadhar = isAadharDoc(docName);
          const pan = isPanDoc(docName);
          const drivingLicense = isDrivingLicenseDoc(docName);
          const placeholder = aadhar
            ? "XXXX XXXX XXXX"
            : pan
            ? "ABCDE1234F"
            : drivingLicense
            ? "MH32 12345678901"
            : docName
            ? `Enter ${docName.toLowerCase()} number`
            : "Enter number";
          const maxLength = aadhar
            ? 14
            : pan
            ? 10
            : drivingLicense
            ? 16
            : undefined;
          const handleNumberChange = (raw: string) => {
            const next = aadhar
              ? formatAadhar(raw)
              : pan
              ? formatPan(raw)
              : drivingLicense
              ? formatDrivingLicense(raw)
              : raw;
            handleDocumentChange(doc.id, { number: next });
          };
          return (
          <div key={doc.id} className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            {/* Number input */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                {docName ? `${docName} number` : "Document Number"}
              </label>
              <input
                type="text"
                value={doc.number}
                onChange={(e) => handleNumberChange(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                inputMode={aadhar ? "numeric" : undefined}
                className="w-full h-10 px-3 border border-[#E0E0E0] rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder:text-[#9E9E9E] outline-none focus:border-[#0DB5B5] transition-colors"
              />
            </div>

            {/* File upload */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                Upload file
              </label>
              <div className="flex items-stretch h-10 border border-[#E0E0E0] rounded-md overflow-hidden">
                {/* Choose File — flush left, fills the full height */}
                <label className="flex items-center cursor-pointer flex-shrink-0">
                  <span className="flex items-center h-full px-3 bg-[#1F1F1F] text-white text-sm 3xl:text-base font-inter whitespace-nowrap border-r border-[#E0E0E0]">
                    Choose File
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleDocumentFileChange(doc.id, e)}
                  />
                </label>
                {/* Filename */}
                <span className="flex-1 flex items-center px-3 text-sm 3xl:text-base text-[#9CA3AF] font-inter truncate">
                  {doc.fileName || "No file chosen"}
                </span>
                {/* Delete */}
                <button
                  type="button"
                  onClick={() => handleDocumentDelete(doc.id)}
                  className="flex items-center justify-center px-3 border-l border-[#E0E0E0] hover:bg-[#FFF5F5] transition-colors flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
          );
        })}

        {/* Add document dropdown */}
        <div className="flex flex-col gap-1.5 max-w-[260px]">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            Add document
          </label>
          <CustomDropdown
            options={availableDocumentOptions}
            value=""
            onChange={handleAddDocument}
            placeholder="Select document type"
          />
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;
